import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { CheckCircle, AlertCircle, XCircle, Info, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Alert, AlertDescription, AlertTitle } from "./alert"

const enhancedAlertVariants = cva(
  "relative w-full rounded-lg border p-4 transition-all duration-200",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground border-border",
        destructive:
          "border-destructive/50 text-destructive bg-destructive/5 dark:border-destructive",
        success:
          "border-success/50 text-success bg-success/5 dark:border-success",
        warning:
          "border-warning/50 text-warning bg-warning/5 dark:border-warning",
        info:
          "border-primary/50 text-primary bg-primary/5 dark:border-primary",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const variantIcons = {
  default: Info,
  success: CheckCircle,
  warning: AlertCircle,
  destructive: XCircle,
  info: Info,
}

interface EnhancedAlertProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof enhancedAlertVariants> {
  title?: string
  description?: string
  dismissible?: boolean
  onDismiss?: () => void
  icon?: React.ReactNode
  hideIcon?: boolean
}

const EnhancedAlert = React.forwardRef<HTMLDivElement, EnhancedAlertProps>(
  ({ className, variant = "default", title, description, dismissible, onDismiss, icon, hideIcon, children, ...props }, ref) => {
    const IconComponent = variantIcons[variant || "default"]
    
    return (
      <Alert
        ref={ref}
        variant={variant}
        className={cn(enhancedAlertVariants({ variant }), className)}
        {...props}
      >
        {!hideIcon && (
          <div className="flex items-start gap-3">
            {icon || <IconComponent className="h-4 w-4 mt-0.5" />}
            <div className="flex-1 min-w-0">
              {title && <AlertTitle>{title}</AlertTitle>}
              {description && <AlertDescription>{description}</AlertDescription>}
              {children}
            </div>
            {dismissible && (
              <button
                onClick={onDismiss}
                className="ml-auto -mx-1.5 -my-1.5 rounded-lg p-1.5 hover:bg-background/50 transition-theme"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
        {hideIcon && (
          <>
            {title && <AlertTitle>{title}</AlertTitle>}
            {description && <AlertDescription>{description}</AlertDescription>}
            {children}
            {dismissible && (
              <button
                onClick={onDismiss}
                className="absolute top-2 right-2 rounded-lg p-1.5 hover:bg-background/50 transition-theme"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </>
        )}
      </Alert>
    )
  }
)
EnhancedAlert.displayName = "EnhancedAlert"

export { EnhancedAlert, type EnhancedAlertProps }